import React, { useState, useContext } from 'react';
import { NotificationContext } from '../../context/NotificationContext';
import { triggerAdminTask } from '../../services/adminService';

const AdminTaskRunner = () => {
  const [propertyId, setPropertyId] = useState('');
  const [taskName, setTaskName] = useState('generateSalesCompsUrls');
  const [lang, setLang] = useState('en');
  const [transferParams, setTransferParams] = useState({ direction: 'import', targetSellerUsername: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const { addNotification } = useContext(NotificationContext);

  const isPropertyIdRequired = !['generateSitemap', 'backupDatabase'].includes(taskName);

  const availableTasks = [
    { value: 'generateSalesCompsUrls', label: 'Generate Sales Comps URLs' },
    { value: 'generateRentCompsUrls', label: 'Generate Rent Comps URLs' },
    { value: 'propertyRecords', label: 'Retrieve: Property Records' },
    { value: 'unemployment', label: 'Retrieve: Unemployment Data' },
    { value: 'medianIncome', label: 'Retrieve: Median Income Data' },
    { value: 'gdpTrends', label: 'Retrieve: GDP Trends' },
    { value: 'hpi', label: 'Retrieve: House Price Index' },
    { value: 'developmentNews', label: 'Retrieve: Development News' },
    { value: 'walkability', label: 'Retrieve: Walkability Score' },
    { value: 'initiateZillowOfflineDataExtraction', label: 'Extract Zillow Offline Data' },
    { value: 'airQuality', label: 'Retrieve: Air Quality' },
    { value: 'crime', label: 'Retrieve: Crime Data' },
    { value: 'schools', label: 'Retrieve: School Data' },
    { value: 'separator', label: '--- Reports ---', disabled: true },
    { value: 'generatePdfFromCache', label: 'Generate PDF from Cache' },
    { value: 'separator', label: '--- System ---', disabled: true },
    { value: 'generateSitemap', label: 'Generate Sitemap' },
    { value: 'backupDatabase', label: 'Backup Database' },
    { value: 'separator', label: '--- Data ---', disabled: true },
    { value: 'transferProperty', label: 'Transfer Property' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isPropertyIdRequired && !propertyId) {
      addNotification('Please enter a Property ID.', 'warning');
      return;
    }

    setIsLoading(true);
    setResponseMessage('');

    try {
      const payload = { taskName };
      if (isPropertyIdRequired) {
        payload.propertyId = propertyId;
      }
      if (taskName === 'generatePdfFromCache') {
        payload.params = { lang };
      }
      if (taskName === 'transferProperty') {
        payload.params = { ...transferParams, sourcePropertyId: propertyId };
      }

      const response = await triggerAdminTask(payload);
      setResponseMessage(response.message);
      addNotification(response.message, 'success');
    } catch (error) {
      const message = error.response?.data?.message || error.message;
      setResponseMessage(`Error: ${message}`);
      addNotification(message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '600px', margin: 'auto' }}>
      <h2>Admin Task Runner</h2>
      <p>This page allows administrators to trigger specific backend jobs manually.</p>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="propertyId">Property ID:</label>
          <input
            type="text"
            id="propertyId"
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
            required={isPropertyIdRequired}
            disabled={!isPropertyIdRequired}
            style={{ width: '100%', padding: '8px', marginTop: '5px', backgroundColor: !isPropertyIdRequired ? '#e9ecef' : 'white' }}
          />
        </div>
        <div style={{ marginBottom: '15px' }}>
          <label htmlFor="taskName">Task:</label>
          <select id="taskName" value={taskName} onChange={(e) => setTaskName(e.target.value)} style={{ width: '100%', padding: '8px', marginTop: '5px' }}>
            {availableTasks.map((task) => (
              <option key={task.value} value={task.value} disabled={task.disabled}>
                {task.label}
              </option>
            ))}
          </select>
        </div>
        {taskName === 'generatePdfFromCache' && (
          <div style={{ marginBottom: '15px' }}>
            <label htmlFor="lang">Language:</label>
            <select id="lang" value={lang} onChange={(e) => setLang(e.target.value)} style={{ width: '100%', padding: '8px', marginTop: '5px' }}>
              <option value="en">English</option>
              <option value="he">Hebrew</option>
            </select>
          </div>
        )}
        {taskName === 'transferProperty' && (
          <div style={{ border: '1px solid #ddd', padding: '15px', marginTop: '15px', borderRadius: '5px' }}>
            <h4>Transfer Property Parameters</h4>
            <div style={{ marginBottom: '10px' }}>
              <label>Direction:</label>
              <div>
                <label style={{ marginRight: '15px' }}>
                  <input type="radio" name="direction" value="import" checked={transferParams.direction === 'import'} onChange={(e) => setTransferParams({ ...transferParams, direction: e.target.value })} />
                  Import (from Remote to Local)
                </label>
                <label>
                  <input type="radio" name="direction" value="export" checked={transferParams.direction === 'export'} onChange={(e) => setTransferParams({ ...transferParams, direction: e.target.value })} />
                  Export (from Local to Remote)
                </label>
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label htmlFor="targetSellerUsername">Target Seller Username (Optional):</label>
              <input
                type="text"
                id="targetSellerUsername"
                value={transferParams.targetSellerUsername}
                onChange={(e) => setTransferParams({ ...transferParams, targetSellerUsername: e.target.value })}
                style={{ width: '100%', padding: '8px', marginTop: '5px' }}
              />
            </div>
          </div>
        )}
        <button type="submit" disabled={isLoading} style={{ padding: '10px 20px' }}>
          {isLoading ? 'Running...' : 'Run Task'}
        </button>
      </form>
      {responseMessage && <div style={{ marginTop: '20px', padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}>{responseMessage}</div>}
    </div>
  );
};

export default AdminTaskRunner;
